if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => { this._showCartModal(response); });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this._showCartModal(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /* ── Cart Confirmation Modal ── */

      _showCartModal(response) {
        // response can be a single item (cart/add.js) or have sections
        const initialItem = response.items ? response.items[0] : response;
        if (!initialItem) return;

        this._updateCartBadge();

        // BHD currency quirk: cart endpoints return prices in unpredictable
        // formats. Source of truth = the price element on the product page,
        // which Liquid rendered correctly via shop.money_format.
        // Read it as text, parse the number, multiply by qty in the modal.
        const readPriceText = (selectors) => {
          for (const sel of selectors) {
            const el = document.querySelector(sel);
            if (el && el.textContent && /\d/.test(el.textContent)) return el.textContent.trim();
          }
          return null;
        };
        const parseAmount = (str) => {
          if (!str) return null;
          const m = str.match(/(\d+(?:[.,]\d+)?)/);
          return m ? parseFloat(m[1].replace(',', '.')) : null;
        };

        // Sale/current price (or regular price if not on sale)
        const currentText = readPriceText([
          '.price__sale .price-item--sale.price-item--last',
          '.price__sale .price-item--sale',
          '.price__regular .price-item--regular',
          '.price .price-item--sale.price-item--last',
          '.price .price-item--regular',
          '[data-product-price]',
        ]);
        // Compare-at (strikethrough) price if shown
        const compareText = readPriceText([
          '.price__sale s.price-item--regular',
          '.price__sale .price-item--regular',
          'del .price-item--regular',
          's.price-item--regular',
        ]);

        initialItem._displayUnitPrice = parseAmount(currentText);
        initialItem._displayUnitCompare = parseAmount(compareText);

        this._renderCartModal(initialItem);
      }

      _renderCartModal(item) {
        const escHtml = (str) => {
          const d = document.createElement('div');
          d.textContent = String(str || '');
          return d.innerHTML;
        };

        // BHD price formatter: input is a major-unit BHD number (e.g. 53), output "53.000 BHD"
        const formatBHD = (val) => (Number(val) || 0).toFixed(3) + ' BHD';

        // Per-unit BHD price captured from the product page DOM (source of truth)
        const unitPrice = item._displayUnitPrice != null ? Number(item._displayUnitPrice) : null;
        const unitCompare = item._displayUnitCompare != null ? Number(item._displayUnitCompare) : null;
        const qty = Number(item.quantity || 1);

        const imgSrc = item.featured_image ? item.featured_image.url : (item.image || '');
        const img = imgSrc
          ? `<img src="${escHtml(imgSrc)}" alt="" class="cls-modal__item-img">`
          : `<div class="cls-modal__item-img cls-modal__item-img--empty"></div>`;

        const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
          ? escHtml(item.variant_title) : '';

        const props = Object.entries(item.properties || {})
          .filter(([k]) => !k.startsWith('_'))
          .map(([k, v]) => `<span class="cls-modal__prop">${escHtml(k)}: ${escHtml(v)}</span>`)
          .join('');

        // Compute display price from page-DOM unit price × qty (the page Liquid
        // already formatted it correctly via shop.money_format).
        let priceHtml;
        if (unitPrice != null) {
          const total = unitPrice * qty;
          const compareTotal = unitCompare != null && unitCompare > unitPrice ? unitCompare * qty : null;
          priceHtml = compareTotal
            ? `<span class="cls-modal__item-price--compare">${formatBHD(compareTotal)}</span> <span class="cls-modal__item-price--sale">${formatBHD(total)}</span>`
            : formatBHD(total);
        } else {
          // Last-resort fallback (uncommon — would mean we couldn't read any price element)
          const finalPrice = item.final_line_price != null ? item.final_line_price : item.price;
          priceHtml = formatBHD(Number(finalPrice));
        }
        const itemKey = escHtml(item.key || '');

        const itemHtml = `
          <div class="cls-modal__item" data-item-key="${itemKey}">
            ${img}
            <div class="cls-modal__item-info">
              <p class="cls-modal__item-title">${escHtml(item.product_title)}</p>
              <p class="cls-modal__item-variant">${variantTitle}</p>
              <div class="cls-modal__props">${props}</div>
            </div>
            <div class="cls-modal__item-right">
              <p class="cls-modal__item-price">${priceHtml}</p>
              <div class="cls-modal__qty">
                <button class="cls-modal__qty-btn cls-modal__qty-minus" data-key="${itemKey}" data-delta="-1">\u2212</button>
                <span class="cls-modal__qty-val" data-key="${itemKey}">${item.quantity}</span>
                <button class="cls-modal__qty-btn cls-modal__qty-plus" data-key="${itemKey}" data-delta="1">+</button>
              </div>
              <button class="cls-modal__remove" data-key="${itemKey}" title="Remove">&#10005;</button>
            </div>
          </div>
        `;

        document.getElementById('cls-cart-modal')?.remove();

        const modal = document.createElement('div');
        modal.id = 'cls-cart-modal';
        modal.className = 'cls-modal';
        modal.innerHTML = `
          <div class="cls-modal__overlay"></div>
          <div class="cls-modal__dialog">
            <div class="cls-modal__header">
              <span class="cls-modal__check">&#10003;</span>
              <h3 class="cls-modal__title">Added!</h3>
              <button class="cls-modal__close" aria-label="Close">&times;</button>
            </div>
            <div class="cls-modal__items">${itemHtml}</div>
            <div class="cls-modal__actions">
              <button class="cls-modal__btn cls-modal__btn--continue">Continue Shopping</button>
              <a href="${window.routes.cart_url}" class="cls-modal__btn cls-modal__btn--cart">View Cart</a>
              <a href="/checkout" class="cls-modal__btn cls-modal__btn--checkout">Checkout</a>
            </div>
          </div>
        `;

        document.body.appendChild(modal);

        // Close events
        modal.querySelector('.cls-modal__close').addEventListener('click', () => modal.remove());
        modal.querySelector('.cls-modal__overlay').addEventListener('click', () => modal.remove());
        modal.querySelector('.cls-modal__btn--continue').addEventListener('click', () => modal.remove());

        // Quantity buttons
        modal.querySelectorAll('.cls-modal__qty-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.key;
            const delta = parseInt(btn.dataset.delta, 10);
            const qtyEl = modal.querySelector(`.cls-modal__qty-val[data-key="${key}"]`);
            if (!key || !qtyEl) return;

            const currentQty = parseInt(qtyEl.textContent, 10) || 1;
            const newQty = Math.max(0, currentQty + delta);

            if (newQty === 0) {
              btn.closest('.cls-modal__item')?.querySelector('.cls-modal__remove')?.click();
              return;
            }

            qtyEl.textContent = newQty;
            btn.disabled = true;

            fetch(window.routes.cart_change_url || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: key, quantity: newQty }),
            })
              .then((r) => r.json())
              .then((cart) => {
                btn.disabled = false;
                document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach((el) => {
                  el.textContent = cart.item_count;
                });
                const lineItem = cart.items?.find((i) => i.key === key);
                if (lineItem) {
                  const priceEl = btn.closest('.cls-modal__item')?.querySelector('.cls-modal__item-price');
                  if (priceEl && unitPrice != null) {
                    const newQtyNum = Number(newQty) || 1;
                    const newTotal = unitPrice * newQtyNum;
                    const newCompareTotal = unitCompare != null && unitCompare > unitPrice
                      ? unitCompare * newQtyNum : null;
                    priceEl.innerHTML = newCompareTotal
                      ? `<span class="cls-modal__item-price--compare">${formatBHD(newCompareTotal)}</span> <span class="cls-modal__item-price--sale">${formatBHD(newTotal)}</span>`
                      : formatBHD(newTotal);
                  }
                }
              })
              .catch(() => { btn.disabled = false; });
          });
        });

        // Remove buttons
        modal.querySelectorAll('.cls-modal__remove').forEach((btn) => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.key;
            if (!key) return;
            btn.disabled = true;
            btn.textContent = '...';
            fetch(window.routes.cart_change_url || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 }),
            })
              .then((r) => r.json())
              .then((cart) => {
                btn.closest('.cls-modal__item')?.remove();
                document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach((el) => {
                  el.textContent = cart.item_count;
                });
                if (!modal.querySelector('.cls-modal__item')) modal.remove();
              })
              .catch(() => { btn.disabled = false; btn.textContent = '\u2715'; });
          });
        });

        // Escape key
        document.addEventListener('keydown', function onEsc(e) {
          if (e.key === 'Escape') { modal.remove(); document.removeEventListener('keydown', onEsc); }
        });

        // Animate in
        requestAnimationFrame(() => modal.classList.add('cls-modal--open'));
      }

      _updateCartBadge() {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            const count = cart.item_count || 0;
            if (!count) return;
            let bubbles = document.querySelectorAll('.cart-count-bubble');
            if (!bubbles.length) {
              document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-mobile').forEach((icon) => {
                const bubble = document.createElement('div');
                bubble.className = 'cart-count-bubble';
                const span = document.createElement('span');
                span.setAttribute('aria-hidden', 'true');
                bubble.appendChild(span);
                icon.appendChild(bubble);
              });
              bubbles = document.querySelectorAll('.cart-count-bubble');
            }
            bubbles.forEach((bubble) => {
              let span = bubble.querySelector('span[aria-hidden="true"]');
              if (!span) {
                span = document.createElement('span');
                span.setAttribute('aria-hidden', 'true');
                bubble.prepend(span);
              }
              span.textContent = count;
            });
          })
          .catch(() => {});
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
